import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Link } from "react-router-dom";

import moment from "moment";

import { IDog, IScore } from "@eagerdog/interfaces";
import { Constants } from "@eagerdog/constants";
import { Config } from "@eagerdog/config";
import { getResultLabel, getResultType } from "@eagerdog/scoring-core";

import { apiService, webSocketService, localStorageService } from "src/services";

import Dropdown, { IOption } from "src/components/Dropdown/Dropdown";
import Button from "src/components/Button/Button";
import Pagination from "src/components/Pagination/Pagination";

import styles from "./index.module.scss";

interface IProps {
  dogs: IDog[]
}

const PAGE_SIZES = [5, 10, 20];

const resultTone = (resultType: string): string => {
  const { qualified, not_qualified, failed, scratched, conflict, disqualified, excused, absent } = Constants.score_result_type;
  const { complete, pending } = Constants.score_status;
  const { pass, fail, abs } = Constants.cwags_scent_result;

  switch (resultType) {
    case qualified: case complete: case "passed": case pass: return styles.qualified;
    case absent: case excused: case "weighed": case abs: case pending: return styles.pending;
    case not_qualified: case disqualified: case failed: case fail: case "nq": return styles.notQualified;
    case scratched: return styles.scratched;
    case conflict: return styles.conflict;
    default: return styles.plain;
  }
};

const resultText = (score: IScore): string => {
  const raw = getResultLabel(score) || "No Result";
  if (raw === "nq" || raw === "ais") return raw.toUpperCase();
  if (raw === "ABS" || raw === "Pass" || raw === "Fail" || raw === "No Result") return raw;
  return raw.charAt(0).toUpperCase() + raw.slice(1);
};

const statusText = (status?: string): string => {
  if (!status) return "";
  return status.replace(/_/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
};

const sameId = (left?: string, right?: string) => String(left ?? "") === String(right ?? "");

const showTime = (value?: string | Date) => {
  const parsed = value ? new Date(value).getTime() : NaN;
  return Number.isNaN(parsed) ? 0 : parsed;
};

function CalendarIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <path d="M16 2v4M8 2v4M3 10h18" />
    </svg>
  );
}

const UserScoresTab = ({ dogs }: IProps) => {
  const [loaded, setLoaded] = useState<boolean>(false);
  const [failed, setFailed] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const [selectedEvent, setSelectedEvent] = useState<IOption>({ value: "All", id: "All" });
  const [selectedShow, setSelectedShow] = useState<IOption>({ value: "All", id: "All" });
  const [selectedDog, setSelectedDog] = useState<IOption>({ value: "All", id: "All" });

  const [eventOptions, setEventOptions] = useState<IOption[]>([]);
  const [allShowOptions, setAllShowOptions] = useState<IOption[]>([]);
  const [showOptions, setShowOptions] = useState<IOption[]>([]);
  const [dogOptions, setDogOptions] = useState<IOption[]>([]);

  const allScoresRef = useRef<IScore[]>([]);
  const allShowOptionsRef = useRef<IOption[]>([]);

  const [scores, setScores] = useState<IScore[]>([]);

  const isComponentMountedRef = useRef(true);

  const getScores = useCallback(() => {
    apiService.getOwnerScores()
      .then((nextScores) => {
        const filteredScores = nextScores.filter(score => score.show_type !== Constants.show_type.conformation);
        allScoresRef.current = filteredScores;
        setScores(filteredScores);
        setFailed(false);
        setLoaded(true);

        if (!eventOptions.length) {
          const events = filteredScores.reduce((agg: IOption[], score) => {
            const eventId = String(score.event_id || "");
            if (eventId && !agg.find((option) => option.id === eventId)) {
              agg.push({
                value: score.event_title || "Untitled Event",
                id: eventId,
                data: score,
              });
            }
            return agg;
          }, []);
          setEventOptions([{ value: "All", id: "All" }, ...events]);
        }

        if (!allShowOptions.length) {
          const shows = filteredScores.reduce((agg: IOption[], score) => {
            const showId = String(score.show_id || "");
            if (showId && !agg.find((option) => option.id === showId)) {
              agg.push({
                value: [score.show_type, score.show_name, `(${moment(score.show_date).format("MM/DD/YY, ddd, h:mm a")})`].join(" "),
                id: showId,
                data: score,
              });
            }
            return agg;
          }, []);
          const allShows = [{ value: "All", id: "All" }, ...shows];
          allShowOptionsRef.current = allShows;
          setAllShowOptions(allShows);
          setShowOptions(allShows);
        }
      })
      .catch(() => {
        setFailed(true);
        setLoaded(true);
      });
  }, [eventOptions.length, allShowOptions.length]);

  const getScoresRef = useRef(getScores);
  useEffect(() => {
    getScoresRef.current = getScores;
  }, [getScores]);

  const handleEventSelect = (_e: any, event: IOption) => {
    setSelectedEvent(event);
    setSelectedShow({ value: "All", id: "All" });
    setPage(1);

    if (event.id === "All") {
      setShowOptions(allShowOptionsRef.current);
    } else {
      const eventScores = allScoresRef.current.filter((score) => sameId(String(score.event_id), event.id));
      const eventShowIds = new Set(eventScores.map((score) => String(score.show_id)));
      setShowOptions(allShowOptionsRef.current.filter((showOption) =>
        showOption.id === "All" || eventShowIds.has(showOption.id)
      ));
    }
  };

  const handleShowSelect = (_e: any, show: IOption) => {
    setSelectedShow(show);
    setPage(1);
  };

  const handleDogSelect = (_e: any, dog: IOption) => {
    setSelectedDog(dog);
    setPage(1);
  };

  useEffect(() => {
    if (!loaded) {
      getScores();
    }
  }, [loaded, getScores]);

  useEffect(() => {
    if (!dogOptions.length && dogs.length) {
      const options = dogs.map((dog) => ({
        value: [dog.call_name, dog.registered_name].join(" / "),
        id: String(dog._id),
      }));
      setDogOptions([{ value: "All", id: "All" }, ...options]);
    }
  }, [dogs, dogOptions]);

  useEffect(() => {
    if (!allShowOptions || allShowOptions.length <= 1) {
      return;
    }

    let cleanupFunctions: (() => void)[] = [];

    const setupWebSocket = async () => {
      try {
        const token = localStorageService.getItem(localStorageService.ACCESS_TOKEN);
        if (!token) {
          return;
        }

        await webSocketService.connect({
          url: Config.web.REACT_APP_API_URL || "http://localhost:3001",
          namespace: "/run-order",
          token,
        });

        const unsubscribeStatus = webSocketService.onConnectionStatus((status) => {
          if (status.connected && isComponentMountedRef.current) {
            allShowOptions.forEach(showOption => {
              if (showOption.id !== "All" && showOption.data) {
                const score = showOption.data;
                if (score.event_id && score.show_id) {
                  webSocketService.joinRunOrderRoom({
                    eventId: score.event_id,
                    showId: score.show_id,
                    ring: "Ring 1",
                  });
                }
              }
            });
          }
        });
        cleanupFunctions.push(unsubscribeStatus);

        const unsubscribeUpdates = webSocketService.onRunOrderUpdate((data) => {
          if (!isComponentMountedRef.current) {
            return;
          }
          if (data.reload) {
            getScoresRef.current();
          }
        });
        cleanupFunctions.push(unsubscribeUpdates);
      } catch (error) {
        // WebSocket setup failed silently
      }
    };

    setupWebSocket();

    return () => {
      isComponentMountedRef.current = false;
      cleanupFunctions.forEach(fn => fn());
      webSocketService.leaveRunOrderRoom();
    };
  }, [allShowOptions]);

  const visibleScores = useMemo(() => {
    return scores
      .filter((score) => {
        if (selectedEvent.id !== "All" && !sameId(String(score.event_id), selectedEvent.id)) return false;
        if (selectedShow.id !== "All" && !sameId(String(score.show_id), selectedShow.id)) return false;
        if (selectedDog.id !== "All" && !sameId(String(score.dog_id), selectedDog.id)) return false;
        return true;
      })
      .sort((left, right) => showTime(right.show_date) - showTime(left.show_date));
  }, [scores, selectedEvent.id, selectedShow.id, selectedDog.id]);

  const pageScores = visibleScores.slice((page - 1) * pageSize, page * pageSize);
  const filtersActive = selectedEvent.id !== "All" || selectedShow.id !== "All" || selectedDog.id !== "All";

  const resetFilters = useCallback(() => {
    setSelectedEvent({ id: "All", value: "All" });
    setSelectedShow({ id: "All", value: "All" });
    setSelectedDog({ id: "All", value: "All" });
    setShowOptions(allShowOptionsRef.current);
    setPage(1);
  }, []);

  return (
    <div className={styles.UserScoresTab}>
      <div className={styles.filters}>
        <Dropdown className={styles.dropdown} label="Event" options={eventOptions} placeholder="All" searchPlaceholder="Search events" searchable value={selectedEvent} onChange={handleEventSelect} />
        <Dropdown className={styles.dropdown} label="Show" options={showOptions} placeholder="All" value={selectedShow} onChange={handleShowSelect} />
        <Dropdown className={styles.dropdown} label="Dog" options={dogOptions} placeholder="All" value={selectedDog} onChange={handleDogSelect} />
        <Button className={styles.reset} onClick={resetFilters}>Reset</Button>
      </div>
      {loaded && !failed && visibleScores.length > 0 && (
        <Pagination
          page={page}
          pageSize={pageSize}
          total={visibleScores.length}
          pageSizeOptions={PAGE_SIZES}
          itemLabel="results"
          onPageChange={setPage}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(1);
          }}
        >
          <div className={styles.list}>
            {pageScores.map((score) => {
              const hideElement = score.show_type === Constants.show_type.fast_cat || score.show_type === Constants.show_type.cat;
              const hideLevel = hideElement || score.show_type === Constants.show_type.farm_dog;
              const level = score.dog_class?.group_class
                ? `${score.dog_class.level || ""} (${score.dog_class.group_class})`
                : (score.dog_class?.level || "");
              const showName = [score.show_type, score.show_name].filter(Boolean).join(" ");
              const status = statusText(score.status);
              return (
                <article key={String(score._id)} className={styles.card}>
                  <div className={styles.cardTop}>
                    <div className={styles.identity}>
                      <div>
                        <p className={styles.kicker}>Call name</p>
                        <h2>{score.call_name}</h2>
                      </div>
                      <span className={`${styles.badge} ${resultTone(getResultType(score))}`}>{resultText(score)}</span>
                      {status && <span className={styles.status}>{status}</span>}
                    </div>
                  </div>
                  <div className={styles.details}>
                    <div className={styles.detail}>
                      <p className={styles.kicker}>Event / Show</p>
                      {score.event_handle ? (
                        <Link className={styles.event} to={`/e/${score.event_handle}`}>{score.event_title}</Link>
                      ) : (
                        <p className={styles.value}>{score.event_title}</p>
                      )}
                      <p className={styles.show}>{showName}</p>
                    </div>
                    <div className={styles.detail}>
                      <p className={styles.kicker}>Show date</p>
                      <p className={styles.dateValue}>
                        <CalendarIcon />
                        <span>{score.show_date ? moment(score.show_date).format("MMM D, YYYY") : ""}</span>
                      </p>
                    </div>
                    {(!hideElement || !hideLevel) && (
                      <div className={styles.split}>
                        {!hideElement && (
                          <div className={styles.detail}>
                            <p className={styles.kicker}>Element</p>
                            <p className={styles.value}>{score.dog_class?.show_element}</p>
                          </div>
                        )}
                        {!hideLevel && (
                          <div className={styles.detail}>
                            <p className={styles.kicker}>Level</p>
                            <p className={styles.value}>{level}</p>
                          </div>
                        )}
                      </div>
                    )}
                    {!!score.summary_fields?.length && (
                      <div className={styles.stats}>
                        {score.summary_fields.map((field) => (
                          <div key={field.label} className={styles.detail}>
                            <p className={styles.kicker}>{field.label}</p>
                            <p className={styles.value}>{field.value}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        </Pagination>
      )}
      {loaded && !failed && visibleScores.length === 0 && (
        <div className={styles.message}>
          <p>No results found</p>
          {filtersActive && <Button className={styles.reset} onClick={resetFilters}>Reset</Button>}
        </div>
      )}
      {!loaded && <p className={styles.message}>Loading results…</p>}
      {loaded && failed && <p className={styles.message}>Results could not be loaded.</p>}
    </div>
  );
};

export default UserScoresTab;
